import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import ReplayIcon from '@mui/icons-material/Replay';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rowKey = (row) => String(row?.id ?? '');

const displayQuizTitle = (row) => {
  const title = String(row?.quiz_title || '').trim();
  if (title && title.toLowerCase() !== 'quiz') return title;
  return row?.chapter_title || title || 'Quiz';
};

const pickSelected = (answer) =>
  answer?.selectedAnswers || answer?.selected_answers || [];

const pickCorrect = (answer) =>
  answer?.correctAnswers || answer?.correct_answers || [];

const optionText = (options, index) => {
  const option = Array.isArray(options) ? options[Number(index)] : null;
  if (option && typeof option === 'object') return option.text || `Option ${Number(index) + 1}`;
  if (typeof option === 'string') return option;
  return `Option ${Number(index) + 1}`;
};

const letter = (index) => String.fromCharCode(65 + Number(index));

/** Normalize stored values (index "2", or option text) → Set of option index strings */
const resolveOptionIndexSet = (values, options) => {
  const set = new Set();
  const list = Array.isArray(values) ? values : [];
  list.forEach((raw) => {
    if (raw === null || raw === undefined || raw === '') return;
    const value = String(raw).trim();
    set.add(value);
    if (/^\d+$/.test(value)) {
      set.add(String(Number(value)));
      return;
    }
    const byText = (options || []).findIndex((opt) => {
      const text = typeof opt === 'object' && opt !== null ? String(opt.text || '') : String(opt);
      return text.trim() === value;
    });
    if (byText >= 0) set.add(String(byText));
  });
  return set;
};

const formatChoiceSummary = (indexSet, options) => {
  const indexes = [...indexSet]
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n) && n >= 0 && n < (options?.length || 0));
  if (!indexes.length) return '—';
  return indexes
    .map((n) => `${letter(n)}. ${optionText(options, n)}`)
    .join(', ');
};

function StudentStyleQuestion({ answer, index, note, onNoteChange }) {
  const options = Array.isArray(answer.options) ? answer.options : [];
  const selected = resolveOptionIndexSet(pickSelected(answer), options);
  const correct = resolveOptionIndexSet(pickCorrect(answer), options);
  const hasStudentPick = selected.size > 0;
  const unknown = Boolean(answer.unknownStudentAnswer) || !hasStudentPick;
  const isCorrectFlag =
    answer.isCorrect === true || answer.is_correct === true;
  const isWrongFlag =
    answer.isCorrect === false || answer.is_correct === false;
  // Derive from picks when flag missing
  const derivedCorrect =
    hasStudentPick &&
    selected.size === correct.size &&
    [...selected].every((v) => correct.has(v));
  const isCorrect = !unknown && (isCorrectFlag || derivedCorrect);
  const isWrong = !unknown && (isWrongFlag || !derivedCorrect);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: unknown ? 'divider' : isWrong ? '#d32f2f' : '#2e7d32',
        bgcolor: '#fff',
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            label={`Q${index + 1}`}
            sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 700 }}
          />
          <Chip
            size="small"
            label={`${answer.maxMarks ?? answer.max_marks ?? 1} mark`}
            sx={{ bgcolor: '#fff8e1' }}
          />
          {isWrong ? <Chip size="small" color="error" label="Student wrong" /> : null}
          {isCorrect ? <Chip size="small" color="success" label="Student correct" /> : null}
          {unknown ? <Chip size="small" label="No student pick stored" /> : null}
        </Stack>

        <Typography fontWeight={700} fontSize={18} sx={{ lineHeight: 1.4 }}>
          {answer.questionText || answer.question_text}
        </Typography>

        {!unknown ? (
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ color: isWrong ? '#d32f2f' : '#2e7d32', fontWeight: 700 }}>
              Student submitted: {formatChoiceSummary(selected, options)}
              {isWrong ? ' (wrong)' : ' (correct)'}
            </Typography>
            {isWrong ? (
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                Correct answer: {formatChoiceSummary(correct, options)}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            Student&apos;s selected option was not saved on this older attempt. Only the correct
            answer is shown in green. Use <b>Give another attempt</b> — after the student retakes,
            wrong choice = red, correct = green.
          </Alert>
        )}

        <Stack spacing={1.25}>
          {options.map((opt, optionIndex) => {
            const key = String(optionIndex);
            const isSelected = selected.has(key);
            const isCorrectOption = correct.has(key);
            // Rule: wrong student pick → RED; correct option → GREEN; if student picked correct → GREEN only
            let borderColor = '#e0e0e0';
            let bg = '#fff';
            let circleBg = '#fff';
            let circleBorder = '#bdbdbd';
            let circleColor = '#616161';
            let statusIcon = null;

            if (!unknown) {
              if (isCorrectOption) {
                borderColor = '#2e7d32';
                bg = '#e8f5e9';
                circleBg = '#2e7d32';
                circleBorder = '#2e7d32';
                circleColor = '#fff';
                statusIcon = <CheckCircleRoundedIcon sx={{ color: '#2e7d32' }} fontSize="small" />;
              } else if (isSelected) {
                borderColor = '#d32f2f';
                bg = '#ffebee';
                circleBg = '#d32f2f';
                circleBorder = '#d32f2f';
                circleColor = '#fff';
                statusIcon = <CancelRoundedIcon sx={{ color: '#d32f2f' }} fontSize="small" />;
              }
            } else if (isCorrectOption) {
              borderColor = '#2e7d32';
              bg = '#e8f5e9';
              circleBg = '#2e7d32';
              circleBorder = '#2e7d32';
              circleColor = '#fff';
              statusIcon = <CheckCircleRoundedIcon sx={{ color: '#2e7d32' }} fontSize="small" />;
            }

            return (
              <Box
                key={`${answer.questionId}-${optionIndex}`}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '2px solid',
                  borderColor,
                  bgcolor: bg,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: circleBorder,
                    bgcolor: circleBg,
                    color: circleColor,
                    display: 'grid',
                    placeItems: 'center',
                    fontWeight: 800,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {letter(optionIndex)}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography>{optionText(options, optionIndex)}</Typography>
                  {!unknown && isSelected && !isCorrectOption ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f', fontWeight: 700 }}>
                      Student chose this (wrong)
                    </Typography>
                  ) : null}
                  {!unknown && isSelected && isCorrectOption ? (
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                      Student chose this (correct)
                    </Typography>
                  ) : null}
                  {!unknown && !isSelected && isCorrectOption ? (
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                      Correct answer
                    </Typography>
                  ) : null}
                  {unknown && isCorrectOption ? (
                    <Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>
                      Correct answer
                    </Typography>
                  ) : null}
                </Box>
                {statusIcon}
              </Box>
            );
          })}
        </Stack>

        {(answer.explanation || answer.adminExplanation || answer.admin_explanation) && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#e3f2fd' }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <LightbulbOutlinedIcon color="primary" fontSize="small" />
              <Typography variant="body2" color="primary.dark">
                {answer.adminExplanation ||
                  answer.admin_explanation ||
                  answer.explanation}
              </Typography>
            </Stack>
          </Box>
        )}

        <TextField
          label="Faculty note / topic explanation"
          size="small"
          multiline
          minRows={2}
          value={note || ''}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Explain the mistake / topic for this student"
        />
      </Stack>
    </Box>
  );
}

export default function QuizReviewPage() {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [sort, setSort] = useState('newest');
  const [courses, setCourses] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [adminExplanation, setAdminExplanation] = useState('');
  const [questionNotes, setQuestionNotes] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [answerSheetsOnly, setAnswerSheetsOnly] = useState(false);

  const selected = useMemo(
    () => submissions.find((row) => rowKey(row) === String(selectedId)) || submissions[0] || null,
    [submissions, selectedId],
  );

  const answers = useMemo(() => {
    const list = Array.isArray(selected?.answers) ? selected.answers : [];
    // Wrong first (like student review focus), then correct — keep original index
    return list
      .map((answer, index) => ({ answer, index }))
      .sort((a, b) => {
        const aWrong =
          a.answer.isCorrect === false || a.answer.is_correct === false ? 1 : 0;
        const bWrong =
          b.answer.isCorrect === false || b.answer.is_correct === false ? 1 : 0;
        return bWrong - aWrong || a.index - b.index;
      });
  }, [selected]);

  useEffect(() => {
    setQIndex(0);
  }, [selectedId]);

  const loadCourses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/courses/list`, {
        headers: getAuthHeaders(),
      });
      const rows = response.data?.data || response.data?.courses || response.data || [];
      setCourses(Array.isArray(rows) ? rows : []);
    } catch {
      setCourses([]);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/lms/quizzes/submissions`, {
        headers: getAuthHeaders(),
        params: {
          status: 'all',
          includeLegacy: answerSheetsOnly ? 'false' : 'true',
          search: search || undefined,
          courseCode: courseCode || undefined,
          sort,
        },
      });
      let rows = response.data?.data || [];
      if (answerSheetsOnly) {
        rows = rows.filter((row) => !row.is_legacy && row.source !== 'progress');
      }
      setSubmissions(rows);
      setSelectedId((prev) => {
        if (prev && rows.some((row) => rowKey(row) === String(prev))) return prev;
        return rows[0] ? rowKey(rows[0]) : null;
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load quiz reviews');
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCourses();
  }, []);

  useEffect(() => {
    loadSubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseCode, sort, answerSheetsOnly]);

  useEffect(() => {
    if (!selected) return;
    setRemarks(selected.remarks || '');
    setAdminExplanation(selected.admin_explanation || selected.adminExplanation || '');
    const notes = {};
    (selected.answers || []).forEach((answer) => {
      notes[answer.questionId] = answer.adminExplanation || answer.admin_explanation || '';
    });
    setQuestionNotes(notes);
  }, [selected]);

  const saveReview = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (selected.is_legacy || selected.source === 'progress') {
        await axios.post(
          `${API_BASE_URL}/lms/quizzes/submissions/from-progress`,
          {
            progressId: selected.progress_id,
            remarks,
            adminExplanation,
            answers: (selected.answers || []).map((answer) => ({
              questionId: answer.questionId,
              adminExplanation: questionNotes[answer.questionId] || '',
            })),
          },
          { headers: getAuthHeaders() },
        );
      } else {
        await axios.put(
          `${API_BASE_URL}/lms/quizzes/submissions/${selected.id}/review`,
          {
            remarks,
            adminExplanation,
            answers: (selected.answers || []).map((answer) => ({
              questionId: answer.questionId,
              adminExplanation: questionNotes[answer.questionId] || '',
            })),
          },
          { headers: getAuthHeaders() },
        );
      }
      setSuccess('Review saved.');
      await loadSubmissions();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const grantRetake = async () => {
    if (!selected) return;
    setGranting(true);
    setError('');
    setSuccess('');
    try {
      await axios.post(
        `${API_BASE_URL}/lms/quizzes/submissions/${selected.id}/grant-retake`,
        {},
        { headers: getAuthHeaders() },
      );
      setSuccess('Another attempt granted. Student can submit this quiz once more.');
      await loadSubmissions();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to grant retake');
    } finally {
      setGranting(false);
    }
  };

  if (loading && submissions.length === 0) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const current = answers[qIndex];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant="h4" fontWeight={800}>
              Student Quiz Review
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Same review view as the student app — analyse mistakes, add coaching notes, then grant
              one more attempt if needed. Each quiz is one submission unless you allow a retake.
            </Typography>
          </Box>
          <Button variant="outlined" onClick={() => navigate('/dashboard/students')}>
            Back to Students
          </Button>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Search student name or mobile"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadSubmissions();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Course</InputLabel>
            <Select
              label="Course"
              value={courseCode}
              onChange={(e) => setCourseCode(e.target.value)}
            >
              <MenuItem value="">All courses</MenuItem>
              {courses.map((course) => {
                const code = course.course_code || course.courseCode || '';
                const name = course.course_name || course.courseName || code;
                return (
                  <MenuItem key={code} value={code}>
                    {name} ({code})
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(e.target.value)}>
              <MenuItem value="newest">Newest submit</MenuItem>
              <MenuItem value="oldest">Oldest submit</MenuItem>
              <MenuItem value="marks_desc">Highest marks</MenuItem>
              <MenuItem value="marks_asc">Lowest marks</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant={answerSheetsOnly ? 'contained' : 'outlined'}
            color="warning"
            onClick={() => setAnswerSheetsOnly((v) => !v)}
          >
            {answerSheetsOnly ? 'Answer sheets only ✓' : 'Answer sheets only (red/green)'}
          </Button>
          <Button variant="contained" onClick={loadSubmissions}>
            Search
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setSearch('');
              setCourseCode('');
              setSort('newest');
              setAnswerSheetsOnly(false);
              setTimeout(() => loadSubmissions(), 0);
            }}
          >
            Clear
          </Button>
        </Stack>

        <Alert severity="info">
          Colour rule: student&apos;s wrong option = <b style={{ color: '#d32f2f' }}>RED</b>, correct
          option = <b style={{ color: '#2e7d32' }}>GREEN</b>. Older attempts (before answer archive)
          only have marks — click <b>Give another attempt</b>, student retakes once, then red/green
          appears.
        </Alert>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={4}>
            <Stack spacing={1.25}>
              <Typography variant="body2" color="text.secondary">
                {submissions.length} student quiz attempt(s)
              </Typography>
              {submissions.length === 0 ? (
                <Alert severity="info">
                  No attempts found. Search by name/mobile or pick a course.
                </Alert>
              ) : null}
              {submissions.map((row) => (
                <Card
                  key={rowKey(row)}
                  variant={rowKey(selected) === rowKey(row) ? 'elevation' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedId(rowKey(row))}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography fontWeight={800}>
                          {row.student_name || 'Student'}
                        </Typography>
                        <Chip
                          size="small"
                          color={row.passed ? 'success' : 'warning'}
                          label={`${row.awarded_marks ?? 0}/${row.total_marks ?? 0}`}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary">
                        {row.student_phone}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.course_code || row.chapter_course_code || '—'} ·{' '}
                        {row.chapter_title || 'Chapter'}
                      </Typography>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {row.is_legacy ? (
                          <Chip size="small" label="Old attempt" />
                        ) : (
                          <Chip
                            size="small"
                            color="warning"
                            label={`Wrong ${row.wrong_count ?? 0}`}
                          />
                        )}
                        {row.retake_allowed ? (
                          <Chip size="small" color="info" label="Retake open" />
                        ) : (
                          <Chip size="small" label="1 attempt used" />
                        )}
                        <Chip
                          size="small"
                          variant="outlined"
                          label={
                            row.submitted_at
                              ? new Date(row.submitted_at).toLocaleDateString()
                              : '—'
                          }
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            {selected ? (
              <Card sx={{ bgcolor: '#fafafa' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      spacing={1.5}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={800}>
                          {displayQuizTitle(selected)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selected.student_name || 'Student'} · {selected.student_phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {selected.course_code || selected.chapter_course_code} ·{' '}
                          {selected.chapter_title || 'Chapter'} · Attempt{' '}
                          {selected.attempt_number || 1}
                        </Typography>
                      </Box>
                      <Stack spacing={1} alignItems={{ sm: 'flex-end' }}>
                        <Chip
                          color={selected.passed ? 'success' : 'warning'}
                          label={`Score ${selected.awarded_marks ?? 0}/${
                            selected.total_marks ?? 0
                          } (${toNumber(selected.percentage)}%)`}
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          startIcon={<ReplayIcon />}
                          disabled={granting || selected.retake_allowed}
                          onClick={grantRetake}
                        >
                          {selected.retake_allowed
                            ? 'Retake already granted'
                            : granting
                              ? 'Granting…'
                              : 'Give another attempt'}
                        </Button>
                      </Stack>
                    </Stack>

                    <Divider />

                    {answers.length === 0 ? (
                      <Alert severity="info">
                        No question review available for this attempt yet.
                      </Alert>
                    ) : (
                      <>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography fontWeight={700}>
                            Question {Math.min(qIndex + 1, answers.length)} of {answers.length}
                            {answers[qIndex]?.answer &&
                            !answers[qIndex].answer.unknownStudentAnswer &&
                            (answers[qIndex].answer.isCorrect === false ||
                              answers[qIndex].answer.is_correct === false)
                              ? ' · Wrong'
                              : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selected.is_legacy || selected.source === 'progress'
                              ? 'Old attempt — student picks not stored'
                              : 'Wrong answers listed first · red = student wrong · green = correct'}
                          </Typography>
                        </Stack>

                        {current ? (
                          <StudentStyleQuestion
                            answer={current.answer}
                            index={current.index}
                            note={questionNotes[current.answer.questionId] || ''}
                            onNoteChange={(value) =>
                              setQuestionNotes((prev) => ({
                                ...prev,
                                [current.answer.questionId]: value,
                              }))
                            }
                          />
                        ) : null}

                        <Stack direction="row" spacing={1}>
                          <Button
                            variant="outlined"
                            disabled={qIndex <= 0}
                            onClick={() => setQIndex((v) => Math.max(0, v - 1))}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="contained"
                            disabled={qIndex >= answers.length - 1}
                            onClick={() =>
                              setQIndex((v) => Math.min(answers.length - 1, v + 1))
                            }
                          >
                            Next
                          </Button>
                        </Stack>
                      </>
                    )}

                    <TextField
                      label="Overall coaching note"
                      multiline
                      minRows={2}
                      value={adminExplanation}
                      onChange={(e) => setAdminExplanation(e.target.value)}
                    />
                    <TextField
                      label="Internal remarks (optional)"
                      size="small"
                      value={remarks}
                      onChange={(e) => setRemarks(e.target.value)}
                    />

                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" onClick={saveReview} disabled={saving}>
                        {saving ? 'Saving…' : 'Save faculty review'}
                      </Button>
                      <Button variant="outlined" onClick={loadSubmissions}>
                        Refresh
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Select a student attempt to review.</Alert>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
}
